import { intersection, isArray, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
  useLoggedInEmployeeId,
  useLoggedInEmployeeProfile,
  useLoggedInUserRoles,
} from '~/components/Auth';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useMyUnitIds } from '~/data/orgunits/useMyUnitIds';
import {
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportSettingsProps,
  AnalyticsReportTypeSettingsConfig,
  AnalyticsReportTypeTeam,
  AnalyticsReportTypes,
} from '~/data/reports';
import { Roles } from '~/data/user';
import {
  AnalyticsReportOptions,
  AnalyticsReportSettings,
} from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/data';
import { UseReportDatasetProps } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/useReportDataset';
import { hasOneOfRoles } from '~/services/Permissions';
import { browserLogInfo } from '~/utils/helper';

export const useValidReportSettingOptions = <P>(
  props: UseReportDatasetProps<P>,
): AnalyticsReportSettings<P> => {
  const { selection: defaultSelection, config: defaultConfig } = props;

  const myEmployeeId = useLoggedInEmployeeId();
  const myRoles = useLoggedInUserRoles();
  const myProfile = useLoggedInEmployeeProfile();
  const { aMyHRUnits, aMyManUnits, aMyAltUnits, aMyDeputyUnits } =
    useMyUnitIds();

  const allMyResponsibleUnits = useMemo(() => {
    return aMyHRUnits
      .concat(aMyManUnits)
      .concat(aMyAltUnits)
      .concat(aMyDeputyUnits);
  }, [aMyHRUnits, aMyManUnits, aMyAltUnits, aMyDeputyUnits]);

  const { legalEntities } = useAllLegalEntities();

  const hasRoleAccess = useCallback(
    (roles: Roles[] | Roles) => {
      return hasOneOfRoles(myRoles, isArray(roles) ? roles : [roles]);
    },
    [myRoles],
  );

  const filterOptions = <TOption>(
    options: TOption[],
    configOptions?: TOption[],
  ): TOption[] => {
    return typeof configOptions !== 'undefined'
      ? intersection(options, configOptions)
      : options;
  };

  const { initialReportSettings, initialReportOptions } = useMemo(() => {
    const resolveDefaultUnitId = (unitIds: number[]) => {
      const userOrgUnitId = myProfile.unit.id;

      if (unitIds.length === 0) {
        browserLogInfo('No valid unit options available for user');
      }

      return unitIds.includes(userOrgUnitId) ? userOrgUnitId : unitIds[0];
    };

    const resolveDefaultLegalEntityId = (legalEntityIds: number[]) => {
      const userLegalEntityId = myProfile.legalEntity.id;

      if (legalEntityIds.length === 0) {
        browserLogInfo('No valid legal entity options available for user');
      }
      return legalEntityIds.includes(userLegalEntityId)
        ? userLegalEntityId
        : legalEntityIds[0];
    };

    const resolveRoleBasedReportOptions = (): AnalyticsReportOptions => {
      // Company & Legal Entity report Types available only if configured in default config
      const types: AnalyticsReportTypes[] = filterOptions(
        [
          AnalyticsReportTypes.Company,
          AnalyticsReportTypes.LegalEntity,
          AnalyticsReportTypes.Employee,
        ],
        defaultConfig?.options?.types,
      );
      const roles: AnalyticsReportRoleScope[] = [];

      // Resolve report Types & Roles options by user role
      if (
        hasRoleAccess([Roles.MANAGER, Roles.ALT_MANAGER, Roles.DEPUTY_MANAGER])
      ) {
        types.push(AnalyticsReportTypes.Team);

        const isAltManagerOnly =
          hasRoleAccess(Roles.ALT_MANAGER) &&
          !hasRoleAccess([Roles.MANAGER, Roles.DEPUTY_MANAGER]);
        const isDeputyManagerOnly =
          hasRoleAccess(Roles.DEPUTY_MANAGER) &&
          !hasRoleAccess([Roles.MANAGER, Roles.ALT_MANAGER]);

        if (isDeputyManagerOnly) {
          roles.push(AnalyticsReportRoleScope.DeputyManager);
        } else if (isAltManagerOnly) {
          roles.push(AnalyticsReportRoleScope.AltManager);
        } else {
          roles.push(AnalyticsReportRoleScope.Manager);
        }
      }

      if (
        hasRoleAccess([
          Roles.HR_ADMIN,
          Roles.HR_ADMIN_LIMITED,
          Roles.HR_STAFF,
          Roles.HR_DEV,
          Roles.HR_PEOP,
          Roles.HR_PEOP_DEV,
          Roles.HR_SALARY,
        ])
      ) {
        types.push(AnalyticsReportTypes.HR);
      }

      types.push(AnalyticsReportTypes.Unit);

      const legalEntityIds = legalEntities.map((le) => le.fLegalEntityId);

      // Include plain employees own unit to unit selection if allowed
      const resolveUnits = (): number[] => {
        if (defaultConfig?.disableUnitOptionsValidation) {
          return uniq(defaultConfig?.options?.units ?? []);
        }

        if (defaultConfig?.enableUnitAccessToEmployeesOwnUnit) {
          return uniq(allMyResponsibleUnits.concat(myProfile.unit.id));
        }
        return allMyResponsibleUnits;
      };

      return {
        types: uniq(types),
        roles: uniq(roles),
        units: resolveUnits(),
        legalEntities: legalEntityIds,
      };
    };

    const filterOptionsByConfig = (
      options: AnalyticsReportOptions,
    ): AnalyticsReportOptions => {
      const { types, roles, units, legalEntities } = options;

      const availableRoles = filterOptions(
        roles,
        defaultConfig?.options?.roles,
      );
      const availableUnits = filterOptions(
        units,
        defaultConfig?.options?.units,
      );

      const availableTypes = filterOptions(
        types,
        defaultConfig?.options?.types,
      ).filter((t) => {
        // Check secondary condition for having a type available
        switch (t) {
          case AnalyticsReportTypes.Unit:
            return availableUnits.length > 0;
          case AnalyticsReportTypes.Team:
            return availableRoles.length > 0;
          default:
            return true;
        }
      });

      return {
        ...options,
        types: availableTypes,
        roles: availableRoles,
        units: availableUnits,
        legalEntities: filterOptions(
          legalEntities,
          defaultConfig?.options?.legalEntities,
        ),
      };
    };

    const roleOptions = filterOptionsByConfig(resolveRoleBasedReportOptions());

    const resolveReportType = (
      defaultType: AnalyticsReportTypes,
      defaultRole: AnalyticsReportTypeTeam['role'],
    ): AnalyticsReportTypeSettingsConfig => {
      return {
        type: defaultType,
        scope: AnalyticsReportScope.Direct,
        employeeId: defaultSelection?.reportType.employeeId ?? myEmployeeId,
        role: defaultRole,
        unitId: resolveDefaultUnitId(roleOptions.units),
        legalEntityId: resolveDefaultLegalEntityId(roleOptions.legalEntities),
      };
    };

    const defaultType =
      !!defaultSelection?.reportType.type &&
      roleOptions.types.includes(defaultSelection.reportType.type)
        ? defaultSelection.reportType.type
        : roleOptions.types[0];

    const initialReportSettings: AnalyticsReportSettingsProps<P> = {
      reportType: resolveReportType(defaultType, roleOptions.roles[0]),
      params: defaultSelection?.params,
    };

    return {
      initialReportSettings,
      initialReportOptions: roleOptions,
    };
  }, [
    defaultConfig,
    hasRoleAccess,
    myEmployeeId,
    allMyResponsibleUnits,
    myProfile,
    defaultSelection,
    legalEntities,
  ]);

  const resolveReportOptions = useCallback(
    (
      config: AnalyticsReportSettings<P>['config'],
    ): AnalyticsReportSettings<P>['config'] => {
      const { types, roles, units } = config.options;

      const filterOptionsIfDefaultIsDefined = <T>(
        options: T[],
        defaultOptions?: T[],
      ): T[] => {
        return typeof defaultOptions !== 'undefined'
          ? intersection(defaultOptions, options)
          : options;
      };

      return {
        ...config,
        options: {
          ...config?.options,
          types: filterOptionsIfDefaultIsDefined(
            types,
            defaultConfig?.options?.types,
          ),
          roles: filterOptionsIfDefaultIsDefined(
            roles,
            defaultConfig?.options?.roles,
          ),
          units: filterOptionsIfDefaultIsDefined(
            units,
            defaultConfig?.options?.units,
          ),
        },
        enableIncludeSubUnits: defaultConfig?.enableIncludeSubUnits ?? true,
      };
    },
    [defaultConfig],
  );

  return {
    selection: initialReportSettings,
    config: resolveReportOptions({ options: initialReportOptions }),
  };
};
